<template>
  <div class="interlayer-page page">
    <div class="illustration"></div>

    <div class="content">
      <div class="title">{{ title }}</div>

      <div v-if="baseline" class="baseline">{{ baseline }}</div>

      <div class="transports">
        <div v-for="transport in definedTransports" :key="transport.key" class="transport-type" :style="{ backgroundColor: transport.color }">
          <i v-if="transport.icon === 'walking'" class="material-icons">directions_walk</i>
          <i v-else :class="[`icon-${transport.icon}`]" />
        </div>
        <div v-if="transports && transports.length === 1" class="transport-name">
          {{ $t(`transportMode.${transports[0].key}`) }}
        </div>
      </div>

      <div v-if="study.withWalkingTimes && transports && transports.length === 1" class="excluded-collaborators">
        <span v-if="study.maximum_travel_time_enabled !== false">{{
          $t(`transportMode.${getPublicTransportNormalized(transports[0].key)}Baseline`, {
            minutes: study[`${getPublicTransportNormalized(transports[0].key)}_maximum_travel_time`],
          })
        }}</span>
        <span>{{
          $t('excludedAddresses', studyCollaboratorsCount - transportCollaboratorsCount, { count: studyCollaboratorsCount - transportCollaboratorsCount })
        }}</span>
      </div>

      <div v-if="comment" class="comment" v-html="comment.replace(/\n/g, '<br/>')"></div>
    </div>

    <div class="footer">
      <div class="focus" :class="{ show: filters }">{{ $t('focusStudyOn') }} : {{ filters }}</div>
      <div class="right-container">
        <div class="logo-line">
          <div class="jll-logo"></div>
          <div class="divider-vertical"></div>
          <div class="commuter-logo">Commuter</div>
        </div>
        <div class="page-number">
          {{ currentPage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'InterlayerPage',

  props: {
    title: {
      type: String,
      required: false,
      default: '',
    },

    site: {
      type: String,
      required: false,
      default: '',
    },

    section: {
      type: String,
      required: false,
      default: '',
    },

    baseline: {
      type: String,
      required: false,
      default: null,
    },

    transports: {
      type: Array,
      required: false,
      default: () => [],
    },

    comment: {
      type: String,
      required: false,
      default: '',
    },

    filters: {
      type: String,
      required: false,
      default: null,
    },

    disableCount: {
      type: [Boolean, Number],
      required: false,
      default: false,
    },

    studyCollaboratorsCount: {
      type: Number,
      required: false,
      default: 0,
    },

    transportCollaboratorsCount: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  data() {
    return {
      currentPage: 0,
    };
  },

  computed: {
    ...mapGetters(['totalPages', 'study']),

    definedTransports() {
      return this.transports.filter(transport => !!transport);
    },
  },

  mounted() {
    if (!this.disableCount) {
      this.currentPage = Number(this.totalPages + 1);
      this.$store.dispatch('setPage');
    } else {
      this.currentPage = this.disableCount;
    }
  },

  methods: {
    getPublicTransportNormalized(transportKey) {
      return transportKey.replaceAll(/\d/g, ''); // replace 'public2025' or 'public2030' or 'public2027' or 'public2031' to 'public'
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

.page {
  width: 297mm;
  height: 209mm;
  background-color: #fff;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  page-break-after: always;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  margin: 64px auto;
  border-radius: 4px;
  overflow: hidden;
}

@media print {
  .page {
    transform: scale(1);
    margin: 0;
    border-radius: 0;
    box-shadow: none;
  }
}

.interlayer-page {
  position: relative;

  .illustration {
    position: absolute;
    top: -70%;
    right: 70%;
    bottom: -25%;
    left: -75%;
    transform: rotate(5deg);
    opacity: 0.3;
    background-image: url('./../../assets/JLL-Brush.png');
    background-repeat: no-repeat;
    background-position: center right;
    background-size: cover;
  }

  .content {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 320px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 8px 64px 16px 0;
    box-sizing: border-box;

    .title {
      display: flex;
      align-items: flex-end;
      height: 128px;
      font-size: 48px;
      font-weight: 600;
      line-height: 64px;
      color: #3c3c3b;
    }

    .transports {
      margin: 16px 0;
      display: flex;
      align-items: center;

      .transport-type {
        display: flex;
        align-items: center;
        justify-content: center;
        $size: 48px;
        width: $size;
        height: $size;
        border-radius: $size;
        line-height: $size;
        text-align: center;
        font-size: 32px;
        margin: 0 8px 0 0;
        color: colors.$white-color;

        .material-icons {
          margin: 0;
        }
      }

      .transport-name {
        margin: 0 0 0 8px;
        font-size: 24px;
      }
    }

    .comment {
      text-align: left;
      min-height: 128px;
      max-height: 320px;
      overflow: hidden;
    }

    .excluded-collaborators {
      display: flex;
      flex-direction: column;
      margin-bottom: 16px;
    }
  }

  .footer {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    color: #9b9b9b;
    padding: 16px 24px 16px 64px;
    font-size: 0.8em;

    .focus {
      opacity: 0;

      &.show {
        opacity: 1;
      }
    }

    .right-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;

      .logo-line {
        display: flex;
        justify-content: flex-end;
        align-items: center;

        .jll-logo {
          width: 64px;
          height: 24px;
          margin: 0;

          background-image: url('./../../assets/JLLLogo-Color.png');
          background-repeat: no-repeat;
          background-position: center right;
          background-size: cover;
        }

        .divider-vertical {
          width: 1px;
          height: 16px;
          border-radius: 2px;
          margin: 2px 16px -2px 12px;
          background: #9b9b9b;
        }

        .commuter-logo {
          font-size: 20px;
          color: colors.$stone-color;
          font-family: 'Golden Plains';
        }
      }
    }

    .page-number {
      text-align: right;
      width: 32px;
      color: #3c3c3b;
    }
  }
}
</style>
